import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { UserContext } from "../../../contexts/user.context";
import { Get, handleError } from "../../../utils/fetch.util";
import NoData from "../../../components/noData/noData.component";
import BackButton from "../../../components/modals/AddAssetModal/BackButton/BackButton";
import { FiSearch } from "react-icons/fi";

interface SecurityTaskLog {
  id: number;
  task_status: string;
  reports_count: number;
  startedAt: string;
  doneAt: string | null;
  createdAt: string;
  task: {
    id: number;
    name: string;
    security_post: {
      id: number;
      name: string;
    };
    point: {
      point_id: number;
      point_name: string;
    };
  };
}

interface SecurityTaskLogsResponse {
  count: number;
  data: SecurityTaskLog[];
}

const SecurityPlanLogs = () => {
  const { securityPostTaskId, securityPostId } = useParams();
  const { t } = useTranslation();
  const { getValidToken } = useContext(UserContext);
  
  const [logs, setLogs] = useState<SecurityTaskLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<{ 
    search: string;
    date: string;
    task_id: string | null;
    security_post_id: string | null;
  }>({ 
    search: "", 
    date: "",
    task_id: null,
    security_post_id: null
  });
  const [allTasks, setAllTasks] = useState<{id: number, name: string}[]>([]);
  const [securityPlans, setSecurityPlans] = useState<any[]>([]);
  const [pagination, setPagination] = useState<{
    page: number;
    pageItemsCount: number;
    count: number;
  }>({ page: 1, pageItemsCount: 10, count: 0 });

  const fetchLogs = async () => {
    setIsLoading(true);
    const query: string[] = [];
    
    query.push(`page=${pagination.page}`);
    query.push(`pageItemsCount=${pagination.pageItemsCount}`);
    
    if (securityPostId) query.push(`postId=${securityPostId}`);
    if (securityPostTaskId) query.push(`taskId=${securityPostTaskId}`);
    if (filters.search.length > 0) query.push(`search=${filters.search}`);
    if (filters.date.length > 0) query.push(`date=${filters.date}`);
    if (filters.task_id) query.push(`task_id=${filters.task_id}`);
    if (filters.security_post_id) query.push(`security_post_id=${filters.security_post_id}`);

    try {
      const res = await Get(
        `security-post-task/get-security-post-task-logs?${query.join("&")}`,
        getValidToken()
      );
      
      if (res?.error || !res?.data) {
        handleError(res);
        setIsLoading(false);
        return;
      }
      
      const responseData = res as SecurityTaskLogsResponse;
      setLogs(responseData.data);
      setPagination((prev) => ({ ...prev, count: responseData.count }));
    } catch (error) {
      console.error("Error fetching security task logs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllTasks = async () => {
    try {
      const query: string[] = [];
      if (filters.security_post_id) query.push(`security_post_id=${filters.security_post_id}`);

      const res = await Get(
        `security-post-task/get-all-tasks?${query.join("&")}`,
        getValidToken()
      );
      if (res?.error || !res?.data) {
        handleError(res);
        return;
      }
      setAllTasks(res.data);
    } catch (error) {
      console.error("Error fetching all tasks:", error);
    }
  };

  const getSecurityPlans = async () => {
    try {
      const res = await Get(
        `security-post/get-all-posts`,
        getValidToken()
      );
      if (res?.error || !res?.data) {
        handleError(res);
        return;
      }
      setSecurityPlans(res.data);
    } catch (error) {
      console.error("Error fetching security plans:", error);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line
  }, [pagination.page, filters.search, filters.date, filters.task_id, filters.security_post_id]);
  
  useEffect(() => {
    fetchAllTasks();
    getSecurityPlans();
    // eslint-disable-next-line
  }, [filters]);

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const utcOffset = -3 * 60 * 60 * 1000;
    const dateWithOffset = new Date(date.getTime() + utcOffset);
    const dateStringWithTime = `${dateWithOffset.toLocaleDateString()} ${dateWithOffset.toLocaleTimeString()}`;
    return dateStringWithTime;
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'done':
        return 'bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm';
      case 'started':
        return 'bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-sm';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm';
      default:
        return 'bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-sm';
    }
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const totalPages = Math.ceil(pagination.count / pagination.pageItemsCount);

  return (
    <div className="container bg-white p-6 px-8">
      <div className="flex items-center gap-3 mt-2 mb-6">
        <BackButton />
        <h1 className="text-5xl">{t("security_task_logs")}</h1>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-5">
        {/* Security Post Filter */}
        <div className="relative w-44">
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            value={filters.security_post_id || ""}
            onChange={(e) => {
              const value = e.target.value || null;
              setFilters(prev => ({ ...prev, security_post_id: value }));
              setPagination(p => ({ ...p, page: 1 }));
            }}
          >
            <option value="">{t("all_security_posts")}</option>
            {securityPlans.map((post) => (
              <option key={post.id} value={post.id}>
                {post.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* Task Filter */}
        {filters.security_post_id && <div className="relative w-44">
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            value={filters.task_id || ""}
            onChange={(e) => {
              const value = e.target.value || null;
              setFilters(prev => ({ ...prev, task_id: value }));
              setPagination(p => ({ ...p, page: 1 }));
            }}
          >
            <option value="">{t("all_tasks")}</option>
            {allTasks.map((task) => (
              <option key={task.id} value={task.id}>
                {task.name}
              </option>
            ))}
          </select>
        </div>}
        
        {/* Search Filter */}
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder={t("search_by_task_name")}
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            value={filters.search}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, search: e.target.value }));
              setPagination(p => ({ ...p, page: 1 }));
            }}
          />
          <button
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-primary"
            style={{
              background: "none",
              border: "none",
              padding: 0,
              margin: 0,
            }}
            onClick={() => {
              setFilters(prev => ({ ...prev, search: prev.search }));
              setPagination(p => ({ ...p, page: 1 }));
            }}
            aria-label="Search"
            tabIndex={-1}
            type="button"
          >
            <FiSearch size={20} />
          </button>
        </div>

        {/* Date Filter */}
        <div className="flex items-center gap-2">
          <label htmlFor="date-filter" className="text-sm font-medium text-gray-700">
            {t("date")}:
          </label>
          <input
            id="date-filter"
            type="date"
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            value={filters.date}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, date: e.target.value }));
              setPagination(p => ({ ...p, page: 1 }));
            }}
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <div className="text-lg">{t("loading")}...</div>
        </div>
      ) : logs.length === 0 ? (
        <NoData message={t("no_logs_found")} />
      ) : (
        <>
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="table-fixed rounded-md border-collapse w-full">
              <thead>
                <tr>
                  <th className="border-2 p-4 py-2">{t("task_name")}</th>
                  <th className="border-2 p-4 py-2">{t("security_post")}</th>
                  <th className="border-2 p-4 py-2">{t("security_point")}</th>
                  <th className="border-2 p-4 py-2">{t("status")}</th>
                  <th className="border-2 p-4 py-2">{t("reports_count")}</th>
                  <th className="border-2 p-4 py-2">{t("answers")}</th>
                  <th className="border-2 p-4 py-2">{t("attendance")}</th>
                  <th className="border-2 p-4 py-2">{t("started_at")}</th>
                  <th className="border-2 p-4 py-2">{t("completed_at")}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td className="border-2 p-4 py-2 font-medium">
                      {log.task?.name}
                    </td>
                    <td className="border-2 p-4 py-2">
                      {log.task?.security_post?.name}
                    </td>
                    <td className="border-2 p-4 py-2">
                      {log.task?.point?.point_name}
                    </td>
                    <td className="border-2 p-4 py-2">
                      <span className={getStatusBadgeClass(log?.task_status)}>
                        {t(log?.task_status)}
                      </span>
                    </td>
                    <td className="border-2 p-4 py-2 text-center">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
                        {log?.reports_count}
                      </span>
                    </td>
                    <td className="border-2 p-4 py-2">
                      <Link
                        className="text-primary"
                        to={`/security-plans/${log.task.security_post.id}/answers/${log.task.id}?createdAt=${log.createdAt}`}
                      >
                        {t("view")}
                      </Link>
                    </td>
                    <td className="border-2 p-4 py-2">
                      <Link
                        className="text-primary"
                        to={`/security-plans-attendance?securityPostId=${log.task.security_post.id}&createdAt=${log.createdAt}`}
                      >
                        {t("view")}
                      </Link>
                    </td>
                    <td className="border-2 p-4 py-2">
                      {formatDateTime(log?.startedAt)}
                    </td>
                    <td className="border-2 p-4 py-2">
                      {formatDateTime(log?.doneAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t("previous")}
              </button>
              
              <span className="px-4 py-2 text-sm text-gray-700">
                {t("page")} {pagination.page} {t("of")} {totalPages}
              </span>
              
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t("next")}
              </button>
            </div>
          )}

          {/* Results Summary */}
          <div className="mt-4 text-sm text-gray-600 text-center">
            {t("showing")} {((pagination.page - 1) * pagination.pageItemsCount) + 1} - {Math.min(pagination.page * pagination.pageItemsCount, pagination.count)} {t("of")} {pagination.count} {t("results")}
          </div>
        </>
      )}
    </div>
  );
};

export default SecurityPlanLogs;
